import { useRef, useState } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { App } from '@/services/db/app/entity';
import { MAX_APP_NAME_LENGTH } from '../utils/platform-state';
import type { SavePlan } from '../utils/save-app';
import { DISCARD_MESSAGE, useDiscardPrompt } from './use-discard-prompt';
import { useAppSave } from './use-app-save';
import { useLeaveGuard } from './use-leave-guard';
import { usePlatformDrafts, type PlatformDraftsController } from './use-platform-drafts';

const CREATE_PERMISSION = 'journeys/push_message_settings/create';
const EDIT_PERMISSION = 'journeys/push_message_settings/edit';
const DELETE_PERMISSION = 'journeys/push_message_settings/delete';

export type UseAppEditorArgs = {
  workspaceId: number;
  app: App | null;
  apps: readonly App[];
  onClose: () => void;
  onSaved: (app: App) => void;
};

function isNameValid(name: string): boolean {
  return name.length > 0 && name.length <= MAX_APP_NAME_LENGTH;
}

function buildPlan(name: string, isRenamed: boolean, app: App | null, platforms: PlatformDraftsController): SavePlan {
  return {
    name,
    ios: platforms.payloads.ios,
    android: platforms.payloads.android,
    renamed: app != null && isRenamed,
    disableIos: platforms.payloads.disableIos,
    disableAndroid: platforms.payloads.disableAndroid,
  };
}

/**
 * The page owns this state rather than the form component because the header's
 * Delete has to be able to leave without being asked to discard first.
 */
export function useAppEditor({ workspaceId, app: savedApp, apps, onClose, onSaved }: UseAppEditorArgs) {
  const { hasPermission } = usePermissions();
  const confirmDiscard = useDiscardPrompt();

  // Keyed off what the form opened on rather than what it has since created,
  // so a retry after a partial create still needs create, not edit.
  const canEdit = hasPermission(EDIT_PERMISSION) === true;
  const canDelete = hasPermission(DELETE_PERMISSION) === true;
  const canSaveForm = savedApp != null ? canEdit : hasPermission(CREATE_PERMISSION) === true;

  // A ref because the drafts need the save's `app`, so they are built after it.
  const platformsRef = useRef<PlatformDraftsController | null>(null);
  const saver = useAppSave({
    workspaceId,
    savedApp,
    onCredentialPersisted: (platform) => platformsRef.current?.clearDraft(platform),
  });
  const { app } = saver;
  const platforms = usePlatformDrafts({ app, canEdit, canDelete, canSaveForm });
  platformsRef.current = platforms;

  const [name, setName] = useState(() => savedApp?.name ?? '');

  const trimmedName = name.trim();
  const isRenamed = trimmedName !== (app?.name ?? '');
  const isDirty = isRenamed || platforms.isDirty;
  const isValid = isNameValid(trimmedName) && platforms.isComplete;

  // A workspace always keeps exactly one default, so the first app created
  // while none exists becomes it. Promoting any later app is its own action.
  const mustBeDefault = app == null && apps.every((entry) => !entry.is_default);

  const resetState = (source: App | null) => {
    setName(source?.name ?? '');
    platforms.reset(source);
    saver.clearError();
  };

  const { allowLeave } = useLeaveGuard({
    isDirty,
    message: DISCARD_MESSAGE,
    onDiscard: () => resetState(app),
  });

  const leaveWith = (source: App | null, exit: (app: App | null) => void) => {
    resetState(source);
    allowLeave();
    exit(source);
  };

  // Set synchronously, because `isSaving` is React state: two events landing in
  // the same tick both read the pre-render value and both get through. A create
  // would register two apps; two Cancels would stack two discard prompts.
  const isSavingRef = useRef(false);
  const isClosingRef = useRef(false);

  const save = async () => {
    if (!isValid || isSavingRef.current || saver.isSaving) {
      return;
    }
    isSavingRef.current = true;
    try {
      const written = await saver.save(buildPlan(trimmedName, isRenamed, app, platforms));
      if (written != null) {
        leaveWith(written, () => onSaved(written));
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  const requestClose = async () => {
    if (isClosingRef.current || isSavingRef.current || saver.isSaving) {
      return;
    }
    isClosingRef.current = true;
    try {
      if (isDirty && !(await confirmDiscard(app?.name))) {
        return;
      }
      leaveWith(app, onClose);
    } finally {
      isClosingRef.current = false;
    }
  };

  return {
    name,
    setName: (value: string) => {
      setName(value);
      saver.clearError();
    },
    error: saver.error,
    mustBeDefault,
    rows: platforms.rows,
    canSaveForm,
    canSave: isDirty && isValid && canSaveForm && !saver.isSaving,
    isDirty,
    isSaving: saver.isSaving,
    save,
    requestClose,
    enablePlatform: platforms.enablePlatform,
    editPlatform: platforms.editPlatform,
    disablePlatform: platforms.disablePlatform,
    allowLeave,
  };
}

export type AppEditorController = ReturnType<typeof useAppEditor>;
